import DashboardLayout from '@/components/DashboardLayout'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Wallet } from 'lucide-react'

type SaleState =
  | 'aguardando_pagamento'
  | 'sem_comissao'
  | 'apurada'
  | 'estornada'
  | 'liquidada'

interface Sale {
  date: string
  label: string
  amount: number
  rate?: number | null
  commission?: number | null
  state: SaleState
}

interface Props {
  period: { from: string; to: string }
  commission_enabled: boolean
  commission_percentage: number
  sales: Sale[]
  totals: { sold: number; earned: number }
}

const brl = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    v,
  )

// O rótulo tem que responder "e o meu dinheiro?" sem precisar de explicação.
const STATE_LABEL: Record<
  SaleState,
  { text: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }
> = {
  aguardando_pagamento: {
    text: 'Aguardando confirmação do pagamento',
    variant: 'outline',
  },
  sem_comissao: { text: 'Sem comissão', variant: 'outline' },
  apurada: { text: 'Apurada', variant: 'default' },
  estornada: { text: 'Estornada', variant: 'destructive' },
  liquidada: { text: 'Paga', variant: 'secondary' },
}

export default function MyCommissions({
  period,
  commission_enabled,
  commission_percentage,
  sales,
  totals,
}: Props) {
  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Minhas comissões</h2>
          <p className="text-sm text-muted-foreground">
            {new Date(`${period.from}T00:00:00`).toLocaleDateString('pt-BR')} a{' '}
            {new Date(`${period.to}T00:00:00`).toLocaleDateString('pt-BR')}
          </p>
        </div>

        {!commission_enabled && (
          <Card>
            <CardContent className="p-4 text-sm text-muted-foreground">
              Você não tem comissão configurada no momento. Vendas antigas que
              já haviam sido registradas com uma porcentagem continuam valendo.
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Vendi no período</CardDescription>
              <CardTitle className="text-2xl">{brl(totals.sold)}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>
                Comissão apurada
                {commission_enabled && ` · ${commission_percentage}% hoje`}
              </CardDescription>
              <CardTitle className="text-2xl">{brl(totals.earned)}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Minhas vendas</CardTitle>
            <CardDescription>
              A comissão só é apurada quando o dono confirma o pagamento. A
              porcentagem é a que valia no dia da venda.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sales.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-10 text-center text-muted-foreground">
                <Wallet className="h-10 w-10 opacity-40" />
                <p className="text-sm">Nenhuma venda sua neste período.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Venda</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-right">%</TableHead>
                    <TableHead className="text-right">Comissão</TableHead>
                    <TableHead>Situação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sales.map((sale, index) => {
                    const state = STATE_LABEL[sale.state]
                    return (
                      <TableRow key={`${sale.label}-${index}`}>
                        <TableCell>
                          <div className="font-medium">{sale.label}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(
                              `${sale.date}T00:00:00`,
                            ).toLocaleDateString('pt-BR')}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {brl(sale.amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {sale.rate != null ? `${sale.rate}%` : '—'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {sale.commission != null ? brl(sale.commission) : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={state.variant}>{state.text}</Badge>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
